import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  Copy,
  Mail,
  MoreVertical,
  Shield,
  ShieldCheck,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { SettingsLayout } from './SettingsLayout';
import { API_URL } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { GradientBorder } from '../../components/ui/gradient-border';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

type TeamRole = 'owner' | 'admin' | 'member';
type AssignableRole = Exclude<TeamRole, 'owner'>;

interface TeamAccount {
  id: string;
  name: string;
  agencyName: string | null;
  email: string;
  planType: string;
  role: TeamRole;
  canManage: boolean;
}

interface TeamMember {
  membershipId: string;
  userId: string;
  name: string;
  email: string;
  image: string | null;
  role: TeamRole;
  status: string;
  createdAt: string;
}

interface TeamInvitation {
  id: string;
  email: string;
  role: AssignableRole;
  status: string;
  expiresAt: string;
  createdAt: string;
}

interface TeamResponse {
  account: TeamAccount;
  members: TeamMember[];
  invitations: TeamInvitation[];
}

const ROLE_LABELS: Record<TeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

const ROLE_COPY: Record<AssignableRole, string> = {
  admin: 'Can manage shared sites and invite employees.',
  member: 'Can see shared agency sites without site credentials.',
};

function roleBadgeClass(role: TeamRole): string {
  if (role === 'owner')
    return 'border-amber-400/30 bg-amber-400/10 text-amber-300 hover:bg-amber-400/10 hover:text-amber-300';
  if (role === 'admin')
    return 'border-sky-400/30 bg-sky-400/10 text-sky-300 hover:bg-sky-400/10 hover:text-sky-300';
  return 'border-white/10 bg-white/5 text-white/60 hover:bg-white/5 hover:text-white/60';
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    return data.error || fallback;
  } catch {
    return fallback;
  }
}

export function SettingsTeamPage() {
  const [accounts, setAccounts] = useState<TeamAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [teamLoading, setTeamLoading] = useState(false);
  const [error, setError] = useState('');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<AssignableRole>('member');
  const [inviting, setInviting] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState('');
  const [lastInviteEmail, setLastInviteEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState('');
  const [savingAgencyName, setSavingAgencyName] = useState(false);
  const [agencyNameSaved, setAgencyNameSaved] = useState(false);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === selectedAccountId) ?? null,
    [accounts, selectedAccountId],
  );

  const loadAccounts = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/v2/cloud/team/accounts`, {
      credentials: 'include',
    });
    if (!res.ok)
      throw new Error(await readError(res, 'Failed to load accounts'));
    const data = (await res.json()) as { accounts: TeamAccount[] };
    setAccounts(data.accounts);

    setSelectedAccountId((current) => {
      if (current && data.accounts.some((account) => account.id === current)) {
        return current;
      }
      const preferred =
        data.accounts.find(
          (account) => account.canManage && account.planType === 'agency',
        ) ?? data.accounts.find((account) => account.canManage);
      return preferred?.id ?? data.accounts[0]?.id ?? '';
    });
  }, []);

  const loadTeam = useCallback(async (accountId: string) => {
    if (!accountId) return;
    setTeamLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/team?accountId=${encodeURIComponent(accountId)}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        throw new Error(await readError(res, 'Failed to load team'));
      }
      setTeam((await res.json()) as TeamResponse);
    } finally {
      // Action handlers await this directly (outside the effect's .finally),
      // so a failed reload must clear the flag itself or the page wedges on
      // "Loading..." after an otherwise-successful mutation.
      setTeamLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadAccounts()
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load team');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAccounts]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedAccountId) return;
    setError('');
    loadTeam(selectedAccountId)
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load team');
          setTeam(null);
        }
      })
      .finally(() => {
        if (!cancelled) setTeamLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadTeam, selectedAccountId]);

  useEffect(() => {
    if (!team?.account) return;
    setAgencyName(team.account.agencyName ?? team.account.name ?? '');
    setAgencyNameSaved(false);
  }, [team?.account.agencyName, team?.account.id, team?.account.name]);

  async function handleInvite() {
    if (!selectedAccountId || inviting) return;
    const email = inviteEmail.trim();
    setInviting(true);
    setError('');
    setLastInviteUrl('');
    setLastInviteEmail('');
    setCopied(false);

    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/team/invitations?accountId=${encodeURIComponent(selectedAccountId)}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            role: inviteRole,
          }),
        },
      );
      if (!res.ok)
        throw new Error(await readError(res, 'Failed to invite member'));
      const data = await res.json();
      setLastInviteUrl(data.invitationUrl ?? '');
      setLastInviteEmail(email);
      setInviteEmail('');
      await loadTeam(selectedAccountId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  }

  async function saveAgencyName() {
    if (!selectedAccountId || savingAgencyName) return;
    const normalizedName = agencyName.trim().replace(/\s+/g, ' ');
    if (!normalizedName) {
      setError('Agency name is required');
      return;
    }

    setSavingAgencyName(true);
    setError('');
    setAgencyNameSaved(false);

    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/team/account?accountId=${encodeURIComponent(selectedAccountId)}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agencyName: normalizedName }),
        },
      );
      if (!res.ok)
        throw new Error(await readError(res, 'Failed to update agency'));
      const data = (await res.json()) as { account: TeamAccount };
      setAgencyName(data.account.agencyName ?? data.account.name);
      setAccounts((current) =>
        current.map((account) =>
          account.id === data.account.id ? data.account : account,
        ),
      );
      setTeam((current) =>
        current ? { ...current, account: data.account } : current,
      );
      setAgencyNameSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agency');
    } finally {
      setSavingAgencyName(false);
    }
  }

  function handleInviteOpenChange(open: boolean) {
    setInviteOpen(open);
    if (!open) {
      setInviteEmail('');
      setLastInviteUrl('');
      setLastInviteEmail('');
      setCopied(false);
    }
  }

  async function copyInviteUrl() {
    if (!lastInviteUrl) return;
    await navigator.clipboard.writeText(lastInviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function updateMemberRole(member: TeamMember, role: AssignableRole) {
    if (!selectedAccountId || role === member.role) return;
    setBusyAction(member.membershipId);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/team/members/${member.membershipId}?accountId=${encodeURIComponent(selectedAccountId)}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        },
      );
      if (!res.ok)
        throw new Error(await readError(res, 'Failed to update member'));
      await loadTeam(selectedAccountId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update member');
    } finally {
      setBusyAction(null);
    }
  }

  async function removeMember(member: TeamMember) {
    if (!selectedAccountId) return;
    setBusyAction(member.membershipId);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/team/members/${member.membershipId}?accountId=${encodeURIComponent(selectedAccountId)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok)
        throw new Error(await readError(res, 'Failed to remove member'));
      await loadTeam(selectedAccountId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setBusyAction(null);
    }
  }

  async function revokeInvitation(invitation: TeamInvitation) {
    if (!selectedAccountId) return;
    setBusyAction(invitation.id);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/team/invitations/${invitation.id}?accountId=${encodeURIComponent(selectedAccountId)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok)
        throw new Error(await readError(res, 'Failed to revoke invite'));
      await loadTeam(selectedAccountId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invite');
    } finally {
      setBusyAction(null);
    }
  }

  const isAgency = selectedAccount?.planType === 'agency';
  const canInvite = Boolean(selectedAccount?.canManage && isAgency);
  const normalizedAgencyName = agencyName.trim().replace(/\s+/g, ' ');
  const savedAgencyName = team?.account.agencyName ?? '';
  const agencyNameChanged = normalizedAgencyName !== savedAgencyName;

  return (
    <SettingsLayout activeTab="team">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-white">Agency</h1>
            <p className="text-sm text-white/50 mt-1">
              Set your agency name and manage employees.
            </p>
          </div>
          <Button onClick={() => setInviteOpen(true)} disabled={!canInvite}>
            <UserPlus className="h-4 w-4" />
            Invite
          </Button>
        </div>

        {accounts.length > 1 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/50">Agency account</span>
            <select
              value={selectedAccountId}
              onChange={(event) => setSelectedAccountId(event.target.value)}
              className="h-9 min-w-64 rounded-md border border-white/10 bg-neutral-900 px-3 text-sm text-white outline-none focus:border-white/30"
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name || account.email} ({ROLE_LABELS[account.role]})
                </option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading || teamLoading ? (
          <div className="py-12 text-center text-sm text-white/50">
            Loading...
          </div>
        ) : !selectedAccount ? (
          <GradientBorder>
            <div className="p-6 text-sm text-white/60">No account found.</div>
          </GradientBorder>
        ) : !isAgency ? (
          <GradientBorder>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-white">
                Agency plan required
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-white/50">
                Agency seats are available on the Agency plan. Your current plan
                is {selectedAccount.planType}.
              </p>
            </div>
          </GradientBorder>
        ) : (
          <>
            <GradientBorder>
              <section className="p-5">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-white">
                    Agency Name
                  </h2>
                  <p className="text-sm text-white/50">
                    Invites will show this name.
                  </p>
                </div>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveAgencyName();
                  }}
                  className="flex flex-col gap-3 sm:flex-row"
                >
                  <label htmlFor="agency-name" className="sr-only">
                    Agency name
                  </label>
                  <input
                    id="agency-name"
                    value={agencyName}
                    onChange={(event) => {
                      setAgencyName(event.target.value);
                      setAgencyNameSaved(false);
                    }}
                    placeholder="Northline Digital"
                    disabled={!selectedAccount.canManage}
                    className="h-10 min-w-0 flex-1 rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white outline-none transition-colors placeholder:text-white/35 focus:border-white/30 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <Button
                    type="submit"
                    disabled={
                      !selectedAccount.canManage ||
                      savingAgencyName ||
                      !normalizedAgencyName ||
                      !agencyNameChanged
                    }
                  >
                    {savingAgencyName
                      ? 'Saving...'
                      : agencyNameSaved
                        ? 'Saved'
                        : 'Save'}
                  </Button>
                </form>
              </section>
            </GradientBorder>

            <GradientBorder>
              <section className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-white">Team</h2>
                    <p className="text-sm text-white/50">
                      Owners and admins can manage shared agency sites.
                    </p>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(team?.members ?? []).map((member) => (
                      <TableRow key={member.membershipId}>
                        <TableCell>
                          <div className="min-w-0">
                            <p className="font-medium text-white">
                              {member.name}
                            </p>
                            <p className="text-xs text-white/45">
                              {member.email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={roleBadgeClass(member.role)}
                          >
                            {member.role === 'admin' ? (
                              <ShieldCheck className="mr-1 h-3 w-3" />
                            ) : (
                              <Shield className="mr-1 h-3 w-3" />
                            )}
                            {ROLE_LABELS[member.role]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-white/50">
                          {formatDate(member.createdAt)}
                        </TableCell>
                        <TableCell>
                          {member.role !== 'owner' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={busyAction === member.membershipId}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    updateMemberRole(member, 'admin')
                                  }
                                >
                                  <ShieldCheck className="h-4 w-4" />
                                  Make admin
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() =>
                                    updateMemberRole(member, 'member')
                                  }
                                >
                                  <Shield className="h-4 w-4" />
                                  Make member
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => removeMember(member)}
                                  className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            </GradientBorder>

            <section>
              <h2 className="mb-3 text-lg font-semibold text-white">
                Pending Invitations
              </h2>
              {(team?.invitations ?? []).length === 0 ? (
                <p className="text-sm text-white/45">No pending invitations.</p>
              ) : (
                <div className="space-y-3">
                  {team!.invitations.map((invitation) => (
                    <GradientBorder key={invitation.id}>
                      <div className="flex items-center justify-between gap-4 p-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-white/40" />
                            <p className="font-medium text-white">
                              {invitation.email}
                            </p>
                            <Badge
                              variant="outline"
                              className={roleBadgeClass(invitation.role)}
                            >
                              {ROLE_LABELS[invitation.role]}
                            </Badge>
                          </div>
                          <p className="mt-1 text-xs text-white/45">
                            Expires {formatDate(invitation.expiresAt)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={busyAction === invitation.id}
                          onClick={() => revokeInvitation(invitation)}
                          title="Revoke invitation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </GradientBorder>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <Dialog open={inviteOpen} onOpenChange={handleInviteOpenChange}>
        <DialogContent
          className="w-[calc(100vw-2rem)] overflow-hidden"
          style={{ maxWidth: '34rem' }}
        >
          <DialogHeader>
            <DialogTitle>Invite Employee</DialogTitle>
            <DialogDescription>
              Invite someone to the selected agency account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-white/70">
                Email
              </label>
              <input
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                type="email"
                placeholder="teammate@example.com"
                className="h-10 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white outline-none focus:border-white/30"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-white/70">
                Role
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(['member', 'admin'] as const).map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setInviteRole(role)}
                    className={`rounded-md border px-3 py-3 text-left transition-colors ${
                      inviteRole === role
                        ? 'border-white/35 bg-white/10 text-white'
                        : 'border-white/10 bg-white/5 text-white/60 hover:border-white/25'
                    }`}
                  >
                    <span className="block text-sm font-medium">
                      {ROLE_LABELS[role]}
                    </span>
                    <span className="mt-1 block text-xs text-white/45">
                      {ROLE_COPY[role]}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {lastInviteUrl && (
              <div className="rounded-md border border-emerald-400/20 bg-emerald-400/10 p-3">
                <p className="text-sm font-medium text-emerald-200">
                  Invite email sent
                </p>
                <p className="mt-1 text-xs leading-relaxed text-emerald-100/75">
                  We sent an invitation to{' '}
                  <span className="break-all">{lastInviteEmail}</span>. If they
                  do not receive it, you can copy and send this invite link
                  manually.
                </p>
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={copyInviteUrl}
                    className="border-emerald-400/30 bg-emerald-400/10 text-emerald-100 hover:bg-emerald-400/15 hover:text-emerald-50"
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copied ? 'Copied' : 'Copy invite link'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleInvite}
              disabled={!inviteEmail.trim() || inviting || !canInvite}
            >
              {inviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsLayout>
  );
}
